/**
 * Calculate standard-specific averages with caching
 * @param {string} standardName - The name of the standard to calculate for
 * @param {Object} WebAssessmentCharts - Reference to the WebAssessmentCharts namespace
 * @param {Object} standardAveragesCache - Cache object for storing calculated averages
 * @param {Object} state - State object containing areAveragesCalculated and lastCalculatedStandard
 * @returns {Object} Calculated averages for the standard
 */
function calculateStandardSpecificAverages(standardName, WebAssessmentCharts, standardAveragesCache, state) {
	// Check if we have cached results for this standard
	if (standardAveragesCache[standardName]) {
		// console.log(`[${performance.now().toFixed(2)}] Using cached averages for ${standardName}`);
		return standardAveragesCache[standardName];
	}

	// Need to calculate for this standard
	if (!state.areAveragesCalculated || state.lastCalculatedStandard !== standardName) {
		const startTime = performance.now();
		// console.log(`[${performance.now().toFixed(2)}] Calculating averages for ${standardName} for the first time...`);
		const qualityData = WebAssessmentCharts.qualityData;
		const processedItems = WebAssessmentCharts.processedItems;

	// Fetch CIS standards data if this is a CIS standard
	// Clear and refetch if switching between different CIS versions
	if (standardName === 'CIS7' || standardName === 'CIS8' || standardName === 'CIS81') {
		const cisStandardId = standardName === 'CIS7' ? 'CIS-7-1' : (standardName === 'CIS8' ? 'CIS-8-0' : 'CIS-8-1');
		const currentCisVersion = WebAssessmentCharts.currentCisVersion;
		
		// Fetch if we don't have it yet, or if we're switching to a different CIS version
		if (!WebAssessmentCharts.cisStandardData || currentCisVersion !== cisStandardId) {
			// console.log(`Fetching CIS standards data for ${cisStandardId}...`);
			WebAssessmentCharts.currentCisVersion = cisStandardId;
			
			// Fetch synchronously for simplicity (can be made async if needed)
			jQuery.ajax({
				url: controlsData.ajax_url,
				type: 'POST',
				dataType: 'json',
				async: false, // Make synchronous to ensure data is available
				data: {
					action: 'api_action',
					year: '2024',
					apis: ['CS'],
					CSLstandardID: cisStandardId
				},
				success: function(response) {
					// console.log('CIS Standards response structure:', response);
					WebAssessmentCharts.cisStandardData = response;
				},
			error: function(error) {
				console.error('Error fetching CIS standards:', error);
			}
			});
		}
	}

		// Get all control sets first to identify which standards we need
		// console.log(`[${performance.now().toFixed(2)}] Starting control sets population...`);
		const controlSetsStartTime = performance.now();
		const allControlSets = WebAssessmentCharts.populateControlSets(qualityData);
		// console.log(`[${performance.now().toFixed(2)}] All control sets populated in ${(performance.now() - controlSetsStartTime).toFixed(2)}ms`);

		// Filter control sets based on the selected standard
		// console.log(`[${performance.now().toFixed(2)}] Filtering control sets for ${standardName}...`);
		// console.log(`[DEBUG] All available standards in allControlSets:`, Object.keys(allControlSets));
		const filteringStartTime = performance.now();
		const controlSets = {};
		Object.keys(allControlSets).forEach(standard => {
			const shouldInclude = standard.includes(standardName) ||
				(standardName === 'NIST11' && standard.includes('NIST_CSF_1_1')) ||
				(standardName === 'NIST2' && standard.includes('NIST_CSF_2_0')) ||
				(standardName === 'CIS7' && standard.includes('CIS_7')) ||
				(standardName === 'CIS8' && standard.includes('CIS_8')) ||
				(standardName === 'CIS81' && standard.includes('CIS_8_1')) ||
				(standardName === 'ISO2013' && standard.includes('ISO_27002_2013')) ||
				(standardName === 'ISO2022' && standard.includes('ISO_27002_2022')) ||
				(standardName === 'NYCRR2' && standard.includes('NYCRR_500_2')) ||
				(standardName === 'UCIS3' && standard.includes('UC_POLICY_IS_3'));

			if (shouldInclude) {
				controlSets[standard] = allControlSets[standard];
				// console.log(`[DEBUG] Included standard: ${standard} (${Object.keys(allControlSets[standard]).length} controls)`);
			} else {
				// console.log(`[DEBUG] Excluded standard: ${standard}`);
			}
		});
		// console.log(`[${performance.now().toFixed(2)}] Control sets filtered in ${(performance.now() - filteringStartTime).toFixed(2)}ms`);
		// console.log(`Filtered to ${Object.keys(controlSets).length} standards for ${standardName}`);
		// console.log(`[DEBUG] Final controlSets keys:`, Object.keys(controlSets));

		// Check if we found any standards to process
		if (Object.keys(controlSets).length === 0) {
			console.warn(`[${performance.now().toFixed(2)}] No standards found for ${standardName} - cannot calculate averages`);
			return {};
		}

		// console.log(`[${performance.now().toFixed(2)}] Starting CRF mappings extraction for ${Object.keys(controlSets).length} standards...`);
		const crfMappingsStartTime = performance.now();
		const crfMappings = {};
		let totalCrfControls = 0;
		Object.entries(controlSets).forEach(([standard, uids]) => {
			// console.log(`Processing ${uids.length} controls for ${standard}`);
			totalCrfControls += uids.length;
			crfMappings[standard] = {};
			uids.forEach(uid => {
				crfMappings[standard][uid] = WebAssessmentCharts.extractCRFMapping(qualityData, uid);
			});
		});
		// console.log(`[${performance.now().toFixed(2)}] CRF mappings extracted in ${(performance.now() - crfMappingsStartTime).toFixed(2)}ms (${totalCrfControls} total controls)`);

		// console.log(`[${performance.now().toFixed(2)}] Starting control values extraction...`);
		const controlValuesStartTime = performance.now();
		const ControlUIDmappingValues = WebAssessmentCharts.extractUniqueControlValues(crfMappings);
		// console.log(`[${performance.now().toFixed(2)}] Control values extracted in ${(performance.now() - controlValuesStartTime).toFixed(2)}ms`);

		// console.log(`[${performance.now().toFixed(2)}] Starting CCC mappings extraction for ${Object.keys(controlSets).length} standards...`);
		const cccMappingsStartTime = performance.now();
		const processedCCCitems = {};
		let totalCccControls = 0;
		Object.entries(controlSets).forEach(([standard, uids]) => {
			totalCccControls += uids.length;
			processedCCCitems[standard] = {};
			// console.log(`[DEBUG] Processing ${uids.length} controls for standard: ${standard}`);
			uids.forEach(uid => {
				// Don't pass a year - let extractCCCMapping use the current assessment's data structure
				const mapping = WebAssessmentCharts.extractCCCMapping(qualityData, uid);
				processedCCCitems[standard][uid] = mapping;
				// if (mapping && mapping.length > 0) {
				// 	console.log(`[DEBUG] Found mapping for ${uid}:`, mapping);
				// }
			});
		});
		WebAssessmentCharts.processedCCCitems = processedCCCitems;
		// console.log(`[${performance.now().toFixed(2)}] CCC mappings extracted in ${(performance.now() - cccMappingsStartTime).toFixed(2)}ms (${totalCccControls} total controls)`);
		// console.log(`[DEBUG] Sample CCC mappings:`, Object.keys(processedCCCitems).slice(0, 2).map(standard => ({
		// 	standard,
		// 	controlCount: Object.keys(processedCCCitems[standard]).length,
		// 	sampleControls: Object.keys(processedCCCitems[standard]).slice(0, 3)
		// })));

		// Determine which mapping to use based on the assessment year
		// Modern assessments (2024, 2025) use CRF mappings (modern safeguard IDs)
		// Legacy assessments (2021, 2022, 2023) use CCC mappings (old CCC IDs)
		let mappingValues;
		
		const assessmentYear = WebAssessmentCharts.assessmentYear || "2025";
		const isModernAssessment = (assessmentYear === "2024" || assessmentYear === "2025" || assessmentYear === "2026");
		
		// console.log(`[DEBUG] Assessment year: ${assessmentYear}`);
		// console.log(`[DEBUG] Is modern assessment: ${isModernAssessment}`);
		// console.log(`[DEBUG] CRF data available: ${Object.keys(ControlUIDmappingValues).length > 0}`);
		// console.log(`[DEBUG] CCC data available: ${Object.keys(processedCCCitems).some(s => Object.keys(processedCCCitems[s]).length > 0)}`);
		
		// For modern assessments (2024/2025), always use CRF mappings
		if (isModernAssessment) {
			// console.log(`[DEBUG] Using CRF mappings for modern assessment (${assessmentYear})`);
			mappingValues = ControlUIDmappingValues;
		} 
		// For legacy assessments (2021/2022/2023), use CCC mappings if available
		else if (Object.keys(processedCCCitems).some(s => Object.keys(processedCCCitems[s]).length > 0)) {
			// console.log(`[DEBUG] Using CCC mappings for legacy assessment (${assessmentYear})`);
			mappingValues = WebAssessmentCharts.extractUniqueControlValues(processedCCCitems);
		} 
		// Fallback to CRF mappings
		else {
			// console.log(`[DEBUG] Using CRF mappings as fallback`);
			mappingValues = ControlUIDmappingValues;
		}
			
		// console.log('Available standards in mappingValues:', Object.keys(mappingValues));
		// console.log('Selected standard name:', standardName);

		// console.log(`[${performance.now().toFixed(2)}] Starting averages calculation...`);
		const calculationStartTime = performance.now();
		// Calculate averages for the filtered standards only
		const averages = WebAssessmentCharts.calculateAverageMaturityScores(processedItems, mappingValues);
		// console.log(`[${performance.now().toFixed(2)}] Averages calculation completed in ${(performance.now() - calculationStartTime).toFixed(2)}ms`);
		// console.log('Calculated averages keys:', Object.keys(averages));
		// console.log('Calculated averages (first 5):', Object.fromEntries(Object.entries(averages).slice(0, 5)));
		
		// Debug: Show CIS-specific keys if this is CIS7
		if (standardName === 'CIS7') {
			const cisKeys = Object.keys(averages).filter(key => key.includes('CIS'));
			// console.log(`[DEBUG] CIS7 - Found ${cisKeys.length} CIS keys:`, cisKeys);
			// console.log(`[DEBUG] CIS7 - Sample CIS values:`, cisKeys.slice(0, 5).map(key => ({ key, value: averages[key] })));
		}

		// Cache the results for this standard (even if empty)
		standardAveragesCache[standardName] = averages;
		state.areAveragesCalculated = true;
		state.lastCalculatedStandard = standardName;
		const totalTime = performance.now() - startTime;
		// console.log(`[${performance.now().toFixed(2)}] Averages calculated and cached for ${standardName} in ${totalTime.toFixed(2)}ms total.`);
		
		return averages;
	}
	
	return standardAveragesCache[standardName] || {};
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
	module.exports = calculateStandardSpecificAverages;
} else if (typeof window !== 'undefined') {
	window.calculateStandardSpecificAverages = calculateStandardSpecificAverages;
}

